import { useFormik } from 'formik';
import * as yup from 'yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { initialValues } from '../constants/constants';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { getData, getErrorMessage, getStatus } from '@/store/slices/helpers/helpers';
import { clearMultipleData, fetchData } from '@/store/slices/fetch-slice';
import { apiKeys, apiMethods, apiStatuses, apiUrls } from '@/store/api/constants';
import { getFormattedPayload, getFormattedResponse } from '@/common/helpers/helpers';
import useGetUserByDivision from '@/common/hooks/useGetUserByDivision';
import useGetSubZoneOptions from '@/common/hooks/useGetSubZoneMaster';

function useCreateEditPrimaryCustomerMaster({ activeEditId, getDistrict, getCity }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const state = useSelector((store) => store);
  const [image, setImage] = useState('');
  const { getUserList } = useGetUserByDivision();
  const { getSubZoneList } = useGetSubZoneOptions();
  const [alertVisible, setAlertVisible] = useState(false);


  const savePrimaryCustomerMasterStatus = getStatus(state, apiKeys.savePrimaryCustomerMaster, '');
  const getPrimeCustDataFromSapStatus = getStatus(state, apiKeys.getPrimeCustDataFromSap, '');
  const savePrimaryCustomerMasterLoading = apiStatuses.pending === savePrimaryCustomerMasterStatus;
  const getEditPrimaryCustomerMasterData = getData(state, apiKeys.getEditPrimaryCustomerMaster, {});
  const getPrimeCustDataFromSapData = getData(state, apiKeys.getPrimeCustDataFromSap, {});
  const getEditPrimaryCustomerMasterStatus = getStatus(state, 
    apiKeys.getEditPrimaryCustomerMaster, '');
  const getEditPrimaryCustomerMasterErrMsg = getErrorMessage(
    state,
    apiKeys.getEditPrimaryCustomerMaster,
    formatMessage({ id: 'failed-to-get-selected-primaryCustomer-details' })
  );

  const validationSchema = yup.object({
    primeCusSapCode: yup.string()
      .required(formatMessage({ id: 'this-field-is-required' }))
      .min(6, formatMessage({ id: 'minimum-6-characters-requirement' }))
      .max(10, formatMessage({ id: 'maximum-10-characters-requirement' }))
      .matches(/^[0-9]+$/, formatMessage({ id: 'only-numeric-characters-allowed' })),
    primeCusName: yup.string().required(formatMessage({ id: 'this-field-is-required' })),
    sapCompany: yup.string().required(formatMessage({ id: 'this-field-is-required' })),
    regionCode: yup.string().required(formatMessage({ id: 'this-field-is-required' })),
    salesOrg: yup.string().required(formatMessage({ id: 'this-field-is-required' })),
    incoterms: yup.string().required(formatMessage({ id: 'this-field-is-required' })),
    primeCusGstNo: yup.string().required(formatMessage({ id: 'this-field-is-required' })),
    sapPrimeCusCategory: yup.string().nullable(formatMessage({ id: 'this-field-is-required' })),
    primeCusCategory: yup.object().required(formatMessage({ id: 'this-field-is-required' })),
    partyCode: yup.mixed().nullable(),
    contactPerson: yup.string().nullable(formatMessage({ id: 'this-field-is-required' })),
    contactNo: yup.string().required(formatMessage({ id: 'this-field-is-required' })),
    primeCusEmail: yup.string().nullable(),
    contactWhatsappNo: yup.string().nullable(),
    sapPriceGroupName: yup.string().required(formatMessage({ id: 'this-field-is-required' })),
    priceGroup: yup.object().required(formatMessage({ id: 'this-field-is-required' })),
    sapSalesHeadName: yup.string().nullable(),
    salesHeadName: yup.object().nullable(),
    sapZoneHeadName: yup.string().nullable(),
    zoneHeadName: yup.object().nullable(),
    sapTeamLeaderName: yup.string().nullable(),
    teamLeaderName: yup.object().nullable(),
    sapSalesSupportRep: yup.string().nullable(),
    salesSupportRep: yup.object().nullable(),
    sapCountry: yup.string().required(formatMessage({ id: 'this-field-is-required' })),
    sapZoneName: yup.string().nullable(),
    zoneId: yup.object().required(formatMessage({ id: 'this-field-is-required' })),
    sapSubzoneName: yup.string().nullable(),
    subzoneId: yup.object().required(formatMessage({ id: 'this-field-is-required' })),
    sapStateName: yup.string().nullable(),
    sapDistrictName: yup.string().nullable(),
    districtId: yup.object().required(formatMessage({ id: 'this-field-is-required' })),
    sapCityName: yup.string().nullable(),
    stateId: yup.object().required(formatMessage({ id: 'this-field-is-required' })),
    cityId: yup.object().required(formatMessage({ id: 'this-field-is-required' })),
    pincode: yup
    .string()
    .required(formatMessage({ id: 'this-field-is-required' }))
    .matches(/^[0-9]+$/, formatMessage({ id: 'only-numerical-characters-allowed' }))
    .length(6, formatMessage({ id: 'exact-6-characters-requirement' })),
    latitude: yup.string().nullable(),
    longitude: yup.string().nullable(),
    address: yup.string().required(formatMessage({ id: 'this-field-is-required' })),
    landmark: yup.string().nullable(),
    file: yup.string().nullable(),
    contactNumber: yup.string().nullable(),
    primeCustCategory: yup.object().nullable(),
    activeStatus: yup.boolean().required(formatMessage({ id: 'this-field-is-required' }))

  });

  const formik = useFormik({
    initialValues,
    validationSchema,
  });
  

  formik.fields = validationSchema?.fields;
  formik.schema = validationSchema;

  const { values } = formik;

  const { districtId, primeCusSapCode, file, zoneId, subzoneId, activeStatus } = values;

  useEffect(() => {
    let payload;
    if (zoneId ) {
      payload = getFormattedPayload({ zoneId });
      getSubZoneList(payload);
    }
    if (activeStatus === undefined) {
      formik.setFieldValue('activeStatus', 1);
    }
  }, [zoneId, getSubZoneList, activeStatus ]);

  useEffect(() => {
    if (subzoneId) {
      const payload = getFormattedPayload({ subzoneId });
      getDistrict(payload);
    }
    getUserList({divisionId:5});
  }, [getDistrict, getUserList, subzoneId]);

  const getPrimeCustDataFromSap = useCallback(() => {
    dispatch(
      fetchData({
        url: apiUrls.getPrimeCustDataFromSap(primeCusSapCode),
        method: apiMethods.GET,
        key: apiKeys.getPrimeCustDataFromSap,
        successMsg: formatMessage({ id: 'data-fetched-successfully-from-sap' }),
        failureMsg: formatMessage({ id: 'data-fetch-failed-from-sap' })
      })
    );
  }, [dispatch, formatMessage, primeCusSapCode]);



  const onSearch = useCallback(() => {
    getPrimeCustDataFromSap(primeCusSapCode);
  }, [primeCusSapCode, getPrimeCustDataFromSap]);

  const handleSapCodeBlur = useCallback(() => {
    formik.setFieldTouched('primeCusSapCode', true, true); 
  }, [formik]);

  useEffect(() => {
    if (primeCusSapCode !== null && primeCusSapCode !== undefined) {
      const trimmedMaterialCode = primeCusSapCode.trim();
      if (trimmedMaterialCode !== '') {
        formik.setFieldValue('primeCusSapCode', trimmedMaterialCode);
      }
    }
  }, [primeCusSapCode, ]);
  
  const handleClick = useCallback(() => {
    onSearch(); 
    handleSapCodeBlur(); 
  }, [onSearch, handleSapCodeBlur]);

  const savePrimaryCustomerMaster = useCallback(
    (values) => {
      dispatch(
        fetchData({
          url: apiUrls.savePrimaryCustomerMaster,
          method: apiMethods.POST,
          key: apiKeys.savePrimaryCustomerMaster,
          successMsg: formatMessage({ id: 'PrimaryCustomer-master-is-saved-successfully' }),
          failureMsg: formatMessage({ id: 'PrimaryCustomer-master-is-failed-to-save' }),
          body: getFormattedPayload(values)
        })
      );
    },
    [dispatch, formatMessage]
  );

  const updatePrimaryCustomerMaster = useCallback(
    (values) => {
      dispatch(
        fetchData({
          url: apiUrls.updatePrimaryCustomerMaster,
          method: apiMethods.PUT,
          key: apiKeys.updatePrimaryCustomerMaster,
          successMsg: formatMessage({ id: 'PrimaryCustomer-master-is-updated-successfully' }),
          failureMsg: formatMessage({ id: 'PrimaryCustomer-master-is-failed-to-update' }),
          body: getFormattedPayload({ ...values, id: activeEditId })
        })
      );
    },
    [dispatch, formatMessage, activeEditId]
  );

  const onChangeZone = useCallback(() => {
    formik.setFieldValue('subzoneId', undefined);
    formik.setFieldValue('districtId', undefined);
    formik.setFieldValue('cityId', undefined);
  }, [formik]);

  const onChangeSubzone = useCallback(() => {
    formik.setFieldValue('districtId', undefined);
    formik.setFieldValue('cityId', undefined);
  }, [formik]);

  const onChangeDistrict = useCallback(() => {
    formik.setFieldValue('cityId', undefined);
  }, [formik]);

  const handleSave = useCallback(async () => {
    if (Object.keys(formik.errors).length === 0) {
      if (activeEditId) {
        await updatePrimaryCustomerMaster(values);
      } else {
        await savePrimaryCustomerMaster(values);
      }
    } else{
      setAlertVisible(true);
    } 
  }, [formik, activeEditId, savePrimaryCustomerMaster, updatePrimaryCustomerMaster, values]);

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]); // Extract base64 part
      reader.onerror = (error) => reject(error);
    });
  };

  const uploadFile = useCallback(
    async (file) => {
      const base64Promises = file.map((file) => readFileAsBase64(file));
      const base64Results = await Promise.all(base64Promises);

      const formData = new FormData();
      base64Results.forEach((base64) => {
        formData.append(`fileName`, base64);
      });
      formData.append('moduleId', '9');
      formData.append(
        'rawFile',
        file.map((file) => file.name)
      );

      const response = await dispatch(
        fetchData({
          url: apiUrls.saveUserImage,
          method: apiMethods.POST,
          key: apiKeys.saveUserImage,
          successMsg: formatMessage({ id: 'user-image-is-saved-successfully' }),
          failureMsg: formatMessage({ id: 'user-image-is-failed-to-save' }),
          body: formData
        })
      );

      if (response.payload && response.payload.response && response.payload.response.data) {
        const imageData = response.payload.response.data;
        setImage(imageData);
      } else {
        console.error('Failed to get image data');
      }
    },
    [dispatch, formatMessage, file]
  );

  const onRemoveCallBack = useCallback(() => {
    const fileName = image[0].imageName;
    dispatch(
      fetchData({
        url: apiUrls.deleteUserImage,
        method: apiMethods.POST,
        key: apiKeys.deleteUserImage,
        successMsg: formatMessage({ id: 'user-image-is-deleted-successfully' }),
        failureMsg: formatMessage({ id: 'user-image-is-deletion-failed' }),
        body: { moduleId: 9, fileName: fileName }
      })
    );
  }, [dispatch, formatMessage, image]);

  useEffect(() => {
    let payload;
    if (districtId) {
      payload = getFormattedPayload({ districtId });
      getCity(payload);
      formik.setFieldValue('designationId', undefined);
    }
  }, [districtId, getCity]);

  const getEditPrimaryCustomerMaster = useCallback(() => {
    dispatch(
      fetchData({
        url: apiUrls.getEditPrimaryCustomerMaster(activeEditId),
        method: apiMethods.GET,
        key: apiKeys.getEditPrimaryCustomerMaster,
        failureMsg: formatMessage({ id: 'failed-to-get-selected-primaryCustomer-details' })
      })
    );
  }, [dispatch, formatMessage, activeEditId]);

  useEffect(() => {
    if (activeEditId) {
      getEditPrimaryCustomerMaster();
    }
  }, [activeEditId, getEditPrimaryCustomerMaster]);

  useEffect(() => {
    if (getEditPrimaryCustomerMasterStatus === apiStatuses.succeeded) {
      formik.setValues(getFormattedResponse(getEditPrimaryCustomerMasterData));
    } else if (getPrimeCustDataFromSapStatus === apiStatuses.succeeded) {
      formik.setValues(getFormattedResponse(getPrimeCustDataFromSapData));
    }
  }, [getEditPrimaryCustomerMasterStatus, getPrimeCustDataFromSapStatus]);


  useEffect(() => {
    return () => {
      dispatch(
        clearMultipleData({
          key: [
            apiKeys.divisionMaster,
            apiKeys.getDepartmentOptions,
            apiKeys.savePrimaryCustomerMaster,
            apiKeys.updatePrimaryCustomerMaster,
            apiKeys.getEditPrimaryCustomerMaster,
            apiKeys.getPrimeCustDataFromSap
          ]
        })
      );
    };
  }, [dispatch]);

  return useMemo(
    () => ({
      formik,
      handleClick,
      handleSave,
      values: formik.values,
      savePrimaryCustomerMasterStatus,
      savePrimaryCustomerMasterLoading,
      getEditPrimaryCustomerMasterData,
      getEditPrimaryCustomerMasterStatus,
      getEditPrimaryCustomerMasterErrMsg,
      getPrimeCustDataFromSapStatus,
      uploadFile,
      onChangeZone,
      onChangeSubzone,
      onChangeDistrict,
      alertVisible,
      onRemoveCallBack
    }),
    [
      formik,
      handleClick,
      handleSave,
      savePrimaryCustomerMasterStatus,
      savePrimaryCustomerMasterLoading,
      getEditPrimaryCustomerMasterData,
      getEditPrimaryCustomerMasterStatus,
      getEditPrimaryCustomerMasterErrMsg,
      getPrimeCustDataFromSapStatus,
      uploadFile,
      onChangeZone,
      onChangeSubzone,
      onChangeDistrict,
      alertVisible,
      onRemoveCallBack
    ]
  );
}

export default useCreateEditPrimaryCustomerMaster;
